import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import { WarrantyBadge, AgeLabel } from './WarrantyBadge';
import { EquipmentForm } from './EquipmentForm';
import { IconInventory, IconPlus, IconTrash } from '../icons';
import { UnresolvedState, shownState } from '../ui/QueryStates';

/**
 * The units this job is working on (PRD v2 §6.7). Tagging is the FIELD half of equipment
 * records: it's what turns a pile of jobs into a per-unit service history, and it only
 * happens if the person standing at the unit can do it in one tap.
 *
 * The warranty badge lives here rather than only on the unit's own page, because the
 * moment it changes a decision is the moment somebody is deciding what to charge.
 */
export function JobEquipmentCard({
  jobId,
  propertyId,
  compact = false,
}: {
  jobId: string;
  propertyId: string;
  compact?: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const onJob = useQuery(trpc.equipment.onJob.queryOptions({ jobId }));
  const [picking, setPicking] = useState(false);

  const untag = useMutation(
    trpc.equipment.untag.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );

  const rows = onJob.data ?? [];

  return (
    <SectionCard
      title={rows.length === 0 ? 'Equipment' : `Equipment (${rows.length})`}
      right={
        <Button variant="ghost" onClick={() => setPicking(true)}>
          <IconPlus size={14} />
          Tag a unit
        </Button>
      }
    >
      {untag.error ? <Notice tone="danger">{untag.error.message}</Notice> : null}

      {onJob.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : onJob.isError ? (
        <p className="text-sm text-[var(--sw-danger-text)]">
          Couldn't load the equipment on this job.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => void onJob.refetch()}
          >
            Try again
          </button>
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Which unit is this job on? Tagging it builds that unit's service history — the
          thing that turns the next call into a replacement quote.
        </p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {rows.map(({ equipment: e }) => (
            <li key={e.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              <Link
                to="/equipment/$equipmentId"
                params={{ equipmentId: e.id }}
                className="min-w-0 flex-1 truncate font-medium hover:underline"
              >
                {e.name}
              </Link>
              <WarrantyBadge
                partsExpiresOn={e.warrantyExpiresOn}
                laborExpiresOn={e.laborWarrantyExpiresOn}
              />
              <button
                type="button"
                aria-label={`Remove ${e.name} from this job`}
                title="Not this one"
                className="shrink-0 rounded-[var(--radius-sm)] p-2 text-muted-foreground hover:bg-surface-2 hover:text-text"
                disabled={untag.isPending}
                onClick={() => untag.mutate({ jobId, equipmentId: e.id, ...idem() })}
              >
                <IconTrash size={15} />
              </button>
              <p
                className={`w-full text-xs text-muted-foreground ${compact ? '' : 'pl-0'}`}
              >
                {[e.make, e.model].filter(Boolean).join(' ') || 'Make/model not recorded'}
                {e.serial ? ` · #${e.serial}` : ''} ·{' '}
                <AgeLabel installedOn={e.installedOn} />
              </p>
            </li>
          ))}
        </ul>
      )}

      {picking ? (
        <TagSheet
          jobId={jobId}
          propertyId={propertyId}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </SectionCard>
  );
}

/**
 * Pick from what's already at the address — every unit, each knowing whether it's tagged.
 * The list is a checklist rather than a search, because a property has four units, not
 * four hundred.
 */
function TagSheet({
  jobId,
  propertyId,
  onClose,
}: {
  jobId: string;
  propertyId: string;
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const picker = useQuery(trpc.equipment.pickerForJob.queryOptions({ jobId }));
  const [adding, setAdding] = useState(false);

  const refresh = () => void qc.invalidateQueries();
  const tag = useMutation(trpc.equipment.tag.mutationOptions({ onSuccess: refresh }));
  const untag = useMutation(trpc.equipment.untag.mutationOptions({ onSuccess: refresh }));

  // The card itself branches correctly; this PICKER never did, so a failed lookup offered
  // "Nothing recorded at this address yet" as an invitation to re-add units that exist.
  const pickerState = shownState(picker, picker.data);
  const units = picker.data ?? [];
  const busy = tag.isPending || untag.isPending;
  const error = tag.error ?? untag.error;

  return (
    <Sheet
      open
      onClose={onClose}
      title="Which units?"
      footer={
        <Button className="w-full" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="space-y-3">
        {error ? <Notice tone="danger">{error.message}</Notice> : null}

        {adding ? (
          <EquipmentForm
            propertyId={propertyId}
            onDone={() => {
              setAdding(false);
              refresh();
            }}
            onCancel={() => setAdding(false)}
          />
        ) : (
          <>
            {pickerState !== 'ready' ? (
              <UnresolvedState
                state={pickerState}
                what="the units at this address"
                icon={IconInventory}
                error={picker.error}
                onRetry={() => void picker.refetch()}
                skeleton="h-16"
              />
            ) : units.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">
                Nothing recorded at this address yet.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {units.map((u) => (
                  <li key={u.id}>
                    <label className="flex min-h-[48px] cursor-pointer items-center gap-3 py-2">
                      <input
                        type="checkbox"
                        className="size-4 shrink-0"
                        checked={u.tagged}
                        disabled={busy}
                        onChange={(e) =>
                          e.target.checked
                            ? tag.mutate({ jobId, equipmentId: u.id, ...idem() })
                            : untag.mutate({ jobId, equipmentId: u.id, ...idem() })
                        }
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">
                          {u.name}
                          {!u.active ? (
                            <span className="text-muted-foreground"> · retired</span>
                          ) : null}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {[u.make, u.model].filter(Boolean).join(' ') || '—'}
                          {u.serial ? ` · #${u.serial}` : ''}
                        </span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
            <Button variant="ghost" className="w-full" onClick={() => setAdding(true)}>
              <IconPlus size={14} />
              Add a unit at this address
            </Button>
            <p className="text-xs text-muted-foreground">
              <IconInventory size={12} className="mr-1 inline" />
              Only units at this job's address can be tagged — a wrong tag would put this
              work in another unit's history.
            </p>
          </>
        )}
      </div>
    </Sheet>
  );
}
